import { useState, useCallback, type InputHTMLAttributes } from 'react'

interface CurrencyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  label?: string
  error?: string
  value: number
  onChange: (value: number) => void
  prefix?: string
}

function formatWithCommas(value: number): string {
  if (isNaN(value) || value === 0) return ''
  return value.toLocaleString('en-GB')
}

function parseFromCommas(value: string): number {
  const cleaned = value.replace(/,/g, '').replace(/[^0-9]/g, '')
  return parseInt(cleaned, 10) || 0
}

export default function CurrencyInput({
  label,
  error,
  value,
  onChange,
  prefix = '£',
  className = '',
  id,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatWithCommas(value))
  const [isFocused, setIsFocused] = useState(false)

  const inputId = id || props.name

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      // Allow only numbers and commas while typing
      const cleanedForDisplay = rawValue.replace(/[^0-9,]/g, '')
      setDisplayValue(cleanedForDisplay)

      const numericValue = parseFromCommas(cleanedForDisplay)
      onChange(numericValue)
    },
    [onChange]
  )

  const handleFocus = useCallback(() => {
    setIsFocused(true)
    // Show raw number without commas when focused for easier editing
    if (value > 0) {
      setDisplayValue(value.toString())
    }
  }, [value])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
    // Format with commas on blur
    setDisplayValue(formatWithCommas(value))
  }, [value])

  // Sync display value when value prop changes externally
  if (!isFocused && formatWithCommas(value) !== displayValue) {
    setDisplayValue(formatWithCommas(value))
  }

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-brand-muted mb-2">
          {label}
          {props.required && <span className="text-accent-teal ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none">
          {prefix}
        </span>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`
            w-full pl-8 pr-4 py-[14px]
            bg-white border border-slate-300 rounded-lg
            text-slate-900 placeholder:text-slate-400
            focus:outline-none focus:border-accent-teal focus:ring-[3px] focus:ring-accent-teal/10
            transition-all duration-300 ease-out
            hover:border-slate-400
            ${error ? 'border-red-500' : ''}
            ${className}
          `}
          {...props}
        />
      </div>
      {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
    </div>
  )
}
